import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, AlertCircle, Info, X, ChevronRight, Sparkles } from "lucide-react";

const SEV = {
 danger: { icon: AlertTriangle, accent: "border-l-[#B35D4C]", text: "text-[#B35D4C]", chip: "bg-[#FAF0EC] text-[#B35D4C]" },
 warn: { icon: AlertCircle, accent: "border-l-[#D4A24C]", text: "text-[#8A6D3B]", chip: "bg-[#FAF7F0] text-[#8A6D3B]" },
 info: { icon: Info, accent: "border-l-[#1B6C62]", text: "text-[#1B6C62]", chip: "bg-[#E6F5F2] text-[#1B6C62]" },
};

/**
 * InsightsPanel. sits at the top of a case (above tabs / inside Chronology).
 * Shows up to N "smart" findings (forensic mismatches, unraised disagreements,
 * stale chases, undated docs, missing bond receipt, lease window etc).
 *
 * Each finding has:
 * - Dismiss (×) → POST /insights/dismiss with the stable id
 * - Address → onAddress(action). parent decides what tab to switch to / what doc to open
 */
export default function InsightsPanel({ caseId, onAddress }) {
 const [items, setItems] = useState([]);
 const [loading, setLoading] = useState(true);
 const [collapsed, setCollapsed] = useState(false);

 const load = async () => {
 setLoading(true);
 try {
 const { data } = await api.get(`/cases/${caseId}/insights`);
 setItems(data.insights || []);
 } finally { setLoading(false); }
 };
 // eslint-disable-next-line react-hooks/exhaustive-deps
 useEffect(() => { load(); }, [caseId]);

 const dismiss = async (it) => {
 setItems(cs => cs.filter(c => c.id !== it.id));
 try {
 await api.post(`/insights/dismiss`, { insight_id: it.id, case_id: caseId });
 } catch (e) {
 // silent. they'll see it again next load
 }
 };

 if (loading || items.length === 0) return null;

 const dangerCount = items.filter(i => i.severity === "danger").length;

 return (
 <div className="card mb-6" data-testid="insights-panel">
 <div className="flex items-center justify-between mb-3">
 <div className="flex items-center gap-2">
 <Sparkles size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
 <div>
 <div className="label-mono">Smart timeline insights</div>
 <h3 className="font-serif text-base text-[#0D2B52]">
 {items.length} thing{items.length === 1 ? "" : "s"} to look at
 {dangerCount > 0 && <span className="text-[#B35D4C]"> · {dangerCount} flagged</span>}
 </h3>
 </div>
 </div>
 <button onClick={() => setCollapsed(c => !c)} className="btn-ghost text-xs" data-testid="insights-toggle">
 {collapsed ? "Show" : "Hide"}
 </button>
 </div>

 {!collapsed && (
 <div className="space-y-2">
 {items.map(it => {
 const cfg = SEV[it.severity] || SEV.info;
 const Icon = cfg.icon;
 return (
 <div key={it.id}
 className={`bg-white border border-[#E5E4DE] border-l-4 ${cfg.accent} p-3 flex gap-3`}
 data-testid={`insight-${it.id}`}>
 <Icon size={16} strokeWidth={1.75} className={`${cfg.text} flex-shrink-0 mt-0.5`} />
 <div className="flex-1 min-w-0">
 <div className="flex items-start justify-between gap-2 mb-0.5">
 <span className="font-serif text-[15px] text-[#0D2B52]">{it.title}</span>
 <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${cfg.chip}`}>{it.severity}</span>
 </div>
 <p className="text-[13px] text-[#333] leading-relaxed">{it.body}</p>
 {it.action && (
 <button onClick={() => onAddress?.(it.action)}
 className="mt-2 inline-flex items-center gap-1 text-[12px] text-[#1B6C62] hover:text-[#0D2B52] font-medium"
 data-testid={`insight-action-${it.id}`}>
 {it.action.label} <ChevronRight size={12} strokeWidth={2} />
 </button>
 )}
 </div>
 <button onClick={() => dismiss(it)} className="btn-ghost p-1 self-start text-[#6B7280] hover:text-[#0D2B52]" title="Dismiss" data-testid={`insight-dismiss-${it.id}`}>
 <X size={14} strokeWidth={1.5} />
 </button>
 </div>
 );
 })}
 </div>
 )}
 </div>
 );
}
